/**
 * The Command Bus: the only way world or editor state changes (CLAUDE.md rule 1).
 *
 * Generic infrastructure (review F-1). It validates requests, runs the owning
 * module's definition, and maintains undo history, the journal and the log.
 * It knows nothing about the world, the editor or any specific command.
 *
 * Every `execute` runs inside a ChangeScheduler batch, so React sees one
 * consistent update per command (review S-5).
 *
 * Built-in commands:
 * - `history.undo` / `history.redo` make undo itself a journaled, dispatchable command.
 * - Transactions are journaled as a single `history.transaction` entry, so replay
 *   reproduces the same undo grouping (see replay.ts).
 * - A transaction merges into the previous one when each of its commands merges
 *   with its counterpart there: one Inspector edit applied to several entities
 *   stays one undo step while typing, as a single-entity edit does.
 */
import type { ChangeScheduler } from "../core/ChangeScheduler";
import type { Log } from "../core/Log";
import type { ReadableStore } from "../core/store";
import { CommandJournal } from "./CommandJournal";
import { originLabel } from "./origins";
import type { JournalSnapshot } from "./CommandJournal";
import { UndoHistory } from "./UndoHistory";
import type { ExecutedCommand, HistoryEntry, UndoState } from "./UndoHistory";
import type {
  AsyncCommandDefinition,
  AsyncCommandResult,
  CommandDefinition,
  CommandOrigin,
  CommandRequest,
  CommandResult,
} from "./types";

export const HISTORY_UNDO = "history.undo";
export const HISTORY_REDO = "history.redo";
export const HISTORY_TRANSACTION = "history.transaction";

/** Payload of the journal entry that records a committed transaction. */
export interface TransactionPayload {
  readonly label: string;
  readonly requests: readonly CommandRequest[];
}

/**
 * A continuous interaction (gizmo drag, field edit) whose commands coalesce.
 * Undo, redo and history clears abort the active session first, so a late
 * frame can never merge into the wrong entry.
 */
export interface InteractionSession {
  readonly name: string;
  readonly active: boolean;
  /** Ends the session normally and seals its undo entry. */
  close(): void;
}

export interface CommandBusOptions {
  scheduler: ChangeScheduler;
  log: Log;
  /** Undo depth cap. Defaults to 200. */
  maxUndo?: number;
  /** Journal entries retained. Defaults to 1000. */
  journalCapacity?: number;
}

interface TransactionScope {
  readonly label: string;
  readonly executed: ExecutedCommand[];
  readonly requests: CommandRequest[];
}

interface ActiveSession {
  readonly name: string;
  readonly onAbort: () => void;
  active: boolean;
}

export class CommandBus {
  private readonly scheduler: ChangeScheduler;
  private readonly log: Log;
  private readonly history: UndoHistory;
  private readonly journalLog: CommandJournal;

  private readonly definitions = new Map<string, CommandDefinition>();
  private readonly asyncDefinitions = new Map<string, AsyncCommandDefinition>();
  private readonly inflight = new Map<string, AbortController>();

  private transactionScope: TransactionScope | null = null;
  private session: ActiveSession | null = null;

  private readonly gates = new Set<(request: CommandRequest) => string | null>();

  constructor(options: CommandBusOptions) {
    this.scheduler = options.scheduler;
    this.log = options.log;
    this.history = new UndoHistory(options.scheduler, options.maxUndo ?? 200);
    this.journalLog = new CommandJournal(options.scheduler, options.journalCapacity ?? 1000);
    this.register(this.historyCommands());
  }

  get journal(): ReadableStore<JournalSnapshot> {
    return this.journalLog.store;
  }

  get undoState(): ReadableStore<UndoState> {
    return this.history.state;
  }

  // ─── Registration ────────────────────────────────────────────────────────

  /** Registers synchronous command definitions. Each type may be registered once. */
  register(definitions: readonly CommandDefinition[]): void {
    for (const definition of definitions) {
      this.assertUnregistered(definition.type);
      this.definitions.set(definition.type, definition);
    }
  }

  /** Registers an async command (e.g. `level.open`). Async commands are never undoable. */
  registerAsync<P>(definition: AsyncCommandDefinition<P>): void {
    this.assertUnregistered(definition.type);
    this.asyncDefinitions.set(definition.type, definition);
  }

  isAsync(type: string): boolean {
    return this.asyncDefinitions.has(type);
  }

  /** All registered command types, e.g. for a console's `help`. */
  types(): string[] {
    return [...this.definitions.keys(), ...this.asyncDefinitions.keys()].sort();
  }

  // ─── Execution ───────────────────────────────────────────────────────────

  execute(request: CommandRequest): CommandResult {
    const definition = this.definitions.get(request.type);
    if (!definition) return this.reject(request, `Unknown command '${request.type}'`);
    const refused = this.refusal(request);
    if (refused !== null) return this.reject(request, refused);

    const parsed = definition.validate(request.payload);
    if (!parsed.ok) return this.reject(request, parsed.error);

    const label = definition.describe(parsed.value);
    try {
      this.scheduler.batch(() => {
        this.apply(definition, { ...request, payload: parsed.value }, label);
      });
      return { ok: true };
    } catch (thrown) {
      const message = errorMessage(thrown);
      this.log.error("Cmd", `${label} failed: ${message}`);
      return { ok: false, error: message };
    }
  }

  /** Runs an async command. A newer request of the same type aborts the in-flight one. */
  async executeAsync(request: CommandRequest): Promise<AsyncCommandResult> {
    const definition = this.asyncDefinitions.get(request.type);
    if (!definition) return this.reject(request, `Unknown async command '${request.type}'`);
    const refused = this.refusal(request);
    if (refused !== null) return this.reject(request, refused);

    const parsed = definition.validate(request.payload);
    if (!parsed.ok) return this.reject(request, parsed.error);

    this.inflight.get(request.type)?.abort();
    const controller = new AbortController();
    this.inflight.set(request.type, controller);

    this.record({ ...request, payload: parsed.value }, definition.describe(parsed.value));

    try {
      await definition.run(parsed.value, controller.signal);
      return { ok: true, cancelled: controller.signal.aborted };
    } catch (thrown) {
      if (controller.signal.aborted) return { ok: true, cancelled: true };
      const message = errorMessage(thrown);
      this.log.error("Cmd", `${definition.describe(parsed.value)} failed: ${message}`);
      return { ok: false, error: message };
    } finally {
      if (this.inflight.get(request.type) === controller) this.inflight.delete(request.type);
    }
  }

  undo(origin?: CommandOrigin): CommandResult {
    return this.execute({ type: HISTORY_UNDO, payload: {}, ...(origin && { origin }) });
  }

  redo(origin?: CommandOrigin): CommandResult {
    return this.execute({ type: HISTORY_REDO, payload: {}, ...(origin && { origin }) });
  }

  // ─── History control ─────────────────────────────────────────────────────

  /** Seals the top undo entry: nothing more merges into it. */
  seal(): void {
    this.history.seal();
  }

  /** Drops all undo/redo history, as a level load does in Unreal. */
  clearHistory(): void {
    this.abortSession();
    this.history.clear();
  }

  /**
   * Groups every undoable command executed inside `fn` into one undo step.
   * Nested transactions flatten into the outermost. If `fn` throws, already
   * applied commands are undone in reverse and the error is rethrown.
   */
  transaction<T>(label: string, fn: () => T): T {
    if (this.transactionScope) return fn();

    const scope: TransactionScope = { label, executed: [], requests: [] };
    this.transactionScope = scope;

    return this.scheduler.batch(() => {
      try {
        const result = fn();
        this.commitTransaction(scope);
        return result;
      } catch (thrown) {
        this.rollback(scope.executed);
        throw thrown;
      } finally {
        this.transactionScope = null;
      }
    });
  }

  /**
   * Adds a gate: while it returns a reason, the requests it names are refused
   * with that reason (Play in Editor keeps the level as it was). Returns a
   * function that removes it.
   */
  addGate(gate: (request: CommandRequest) => string | null): () => void {
    this.gates.add(gate);
    return () => {
      this.gates.delete(gate);
    };
  }

  /** Starts a coalescing interaction. Opening a new session closes the previous one. */
  openSession(name: string, onAbort: () => void): InteractionSession {
    if (this.session?.active) this.closeSession();

    const state: ActiveSession = { name, onAbort, active: true };
    this.session = state;

    return {
      name,
      get active() {
        return state.active;
      },
      close: () => {
        if (this.session === state) this.closeSession();
      },
    };
  }

  // ─── Internals ───────────────────────────────────────────────────────────

  /** The first gate's reason to refuse a request, or null. */
  private refusal(request: CommandRequest): string | null {
    for (const gate of this.gates) {
      const reason = gate(request);
      if (reason !== null) return reason;
    }
    return null;
  }

  /** Applies a validated command and records it in history, journal and log. */
  private apply(definition: CommandDefinition, request: CommandRequest, label: string): void {
    const memo = definition.do(request.payload);
    const executed: ExecutedCommand = { type: definition.type, payload: request.payload, memo };
    const undoable = isUndoable(definition, request.payload);

    if (this.transactionScope) {
      if (undoable) this.transactionScope.executed.push(executed);
      this.transactionScope.requests.push(request);
      return;
    }

    if (!undoable) {
      this.record(request, label, definition.quiet === true);
      return;
    }

    const merged = this.mergeIntoTop([executed], false);
    if (merged) {
      const payload = merged.commands[0]?.payload;
      this.replaceJournal(merged, { ...request, payload }, definition.describe(payload));
      return;
    }

    const entry = this.record(request, label);
    this.history.push({
      label,
      commands: [executed],
      sealed: false,
      grouped: false,
      journalSeq: entry.seq,
    });
  }

  private commitTransaction(scope: TransactionScope): void {
    if (scope.requests.length === 0) return;

    // Only all-undoable transactions merge: then requests and executed commands
    // pair up one to one, and the journal entry can carry the merged payloads.
    const mergeable = scope.executed.length === scope.requests.length;
    const merged = mergeable ? this.mergeIntoTop(scope.executed, true) : null;
    if (merged) {
      const requests = scope.requests.map((request, i) => ({
        ...request,
        payload: merged.commands[i]?.payload,
      }));
      this.replaceJournal(merged, transactionRequest(scope.label, requests), scope.label);
      return;
    }

    const entry = this.record(transactionRequest(scope.label, scope.requests), scope.label);
    if (scope.executed.length > 0) {
      this.history.push({
        label: scope.label,
        commands: scope.executed,
        sealed: !mergeable,
        grouped: true,
        journalSeq: entry.seq,
      });
    }
  }

  /**
   * Coalesces `next` into the unsealed top entry of the same shape, command by
   * command, when every command's definition agrees (same session, same
   * target). Returns the updated entry, or null if anything didn't merge, in
   * which case the entry is untouched.
   */
  private mergeIntoTop(next: readonly ExecutedCommand[], grouped: boolean): HistoryEntry | null {
    const top = this.history.top;
    if (!top || top.sealed || top.grouped !== grouped) return null;
    if (top.commands.length !== next.length) return null;

    const merged: ExecutedCommand[] = [];
    for (const [i, command] of next.entries()) {
      const previous = top.commands[i];
      const definition = this.definitionFor(command.type);
      const combined =
        previous?.type === command.type && definition.merge
          ? definition.merge(previous, command)
          : null;
      if (!combined) return null;
      merged.push({ type: command.type, ...combined });
    }

    top.commands = merged;
    this.history.touched();
    return top;
  }

  /** A merge rewrites its journal entry rather than adding one (F-7). */
  private replaceJournal(entry: HistoryEntry, request: CommandRequest, label: string): void {
    if (entry.journalSeq !== null) this.journalLog.replace(entry.journalSeq, request, label);
  }

  /** Undoes executed commands in reverse order via their definitions. */
  private rollback(executed: readonly ExecutedCommand[]): void {
    for (const command of [...executed].reverse()) {
      this.definitionFor(command.type).undo(command.payload, command.memo);
    }
  }

  private record(request: CommandRequest, label: string, quiet = false) {
    const originSuffix = request.origin ? ` (from ${originLabel(request.origin)})` : "";
    if (quiet) this.log.verbose("Cmd", `${label}${originSuffix}`);
    else this.log.log("Cmd", `${label}${originSuffix}`);
    return this.journalLog.append(request, label);
  }

  private reject(request: CommandRequest, error: string): { ok: false; error: string } {
    this.log.warning("Cmd", `Rejected '${request.type}': ${error}`);
    return { ok: false, error };
  }

  private performUndo(): void {
    this.abortSession();
    const entry = this.history.popUndo();
    if (!entry) return;

    try {
      this.rollback(entry.commands);
    } catch (thrown) {
      this.history.restoreAfterFailedUndo(entry);
      throw thrown;
    }
  }

  private performRedo(): void {
    this.abortSession();
    const entry = this.history.popRedo();
    if (!entry) return;

    // Re-apply in order, capturing fresh memos (state should match the original run).
    entry.commands = entry.commands.map((command) => ({
      ...command,
      memo: this.definitionFor(command.type).do(command.payload),
    }));
  }

  private abortSession(): void {
    const session = this.session;
    if (!session?.active) return;

    session.active = false;
    this.session = null;
    session.onAbort();
    this.history.seal();
  }

  private closeSession(): void {
    const session = this.session;
    if (!session) return;

    session.active = false;
    this.session = null;
    this.history.seal();
  }

  private historyCommands(): CommandDefinition[] {
    const noPayload = () => ({ ok: true as const, value: {} });
    return [
      {
        type: HISTORY_UNDO,
        undoable: false,
        validate: noPayload,
        do: () => {
          this.performUndo();
        },
        undo: () => undefined,
        describe: () => `Undo ${this.history.state.getSnapshot().undoLabel ?? ""}`.trim(),
      },
      {
        type: HISTORY_REDO,
        undoable: false,
        validate: noPayload,
        do: () => {
          this.performRedo();
        },
        undo: () => undefined,
        describe: () => `Redo ${this.history.state.getSnapshot().redoLabel ?? ""}`.trim(),
      },
    ];
  }

  private definitionFor(type: string): CommandDefinition {
    const definition = this.definitions.get(type);
    if (!definition) throw new Error(`No definition registered for '${type}'`);
    return definition;
  }

  private assertUnregistered(type: string): void {
    if (this.definitions.has(type) || this.asyncDefinitions.has(type)) {
      throw new Error(`Command '${type}' is already registered`);
    }
  }
}

function transactionRequest(
  label: string,
  requests: readonly CommandRequest[],
): CommandRequest<TransactionPayload> {
  return { type: HISTORY_TRANSACTION, payload: { label, requests } };
}

function isUndoable(definition: CommandDefinition, payload: unknown): boolean {
  return typeof definition.undoable === "function"
    ? definition.undoable(payload)
    : definition.undoable;
}

function errorMessage(thrown: unknown): string {
  return thrown instanceof Error ? thrown.message : String(thrown);
}
